import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { pickText, pickList, toBilingual, toBilingualList } from "@/lib/bilingual";
import BilingualEditor from "@/components/BilingualEditor";
import { toast } from "sonner";
import { FileText, Upload, Edit3, Save, X } from "lucide-react";

function pdfHref(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API_BASE.replace(/\/api$/, "")}${url}`;
}

export default function Reglas() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [rules, setRules] = useState(null);
  const [pools, setPools] = useState([]);
  const isAdmin = !!user?.is_admin;
  const load = async () => {
    const r = await api.get("/rules");
    setRules(r.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/pools").then((r) => setPools(r.data || [])).catch(() => {});
  }, [isAdmin]);

  if (!rules) return <div className="text-zinc-400">{t("common.loading")}</div>;

  const colors = ["var(--c-lime)", "var(--c-yellow)", "var(--c-pink)", "var(--c-sky)", "var(--c-mauve)", "var(--c-mint)", "var(--c-peach)"];

  const saveLabels = async (kind, ruleId, nextLabel) => {
    // Build override dict by merging current values for all ids of that kind.
    const items = rules[kind] || [];
    const payloadKey = kind === "scoring" ? "rules_scoring_override" : "rules_bonus_override";
    const override = {};
    items.forEach((r) => {
      override[r.id] = r.id === ruleId ? nextLabel : toBilingual(r.label);
    });
    await api.put("/admin/page-content", { [payloadKey]: override });
    await load();
  };

  return (
    <div className="space-y-8" data-testid="reglas-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("rules.kicker")}</div>
        <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">{t("rules.heading")}</h1>
        <p className="text-zinc-300 text-base md:text-lg mt-3 max-w-2xl leading-relaxed">
          {t("rules.subheading")}
        </p>
      </div>

      <div className="space-y-3">
        <SectionTitle text={t("rules.section.points")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rules.scoring.map((rule, i) => (
            <RuleCard
              key={rule.id}
              color={colors[i % colors.length]}
              label={pickText(rule.label, lang)}
              points={rule.points}
              ptsLabel={t("rules.pts")}
              canEdit={isAdmin}
              value={rule.label}
              onSave={(next) => saveLabels("scoring", rule.id, next)}
              testid={`rule-scoring-${rule.id}`}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <SectionTitle text={t("rules.section.bonus")} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {rules.bonus.map((rule, i) => (
            <RuleCard
              key={rule.id}
              color={colors[(i + 3) % colors.length]}
              label={pickText(rule.label, lang)}
              points={rule.points}
              ptsLabel={t("rules.pts")}
              canEdit={isAdmin}
              value={rule.label}
              onSave={(next) => saveLabels("bonus", rule.id, next)}
              testid={`rule-bonus-${rule.id}`}
            />
          ))}
        </div>
      </div>

      <EditableRulesSection
        title={t("rules.section.lock")}
        accent="!"
        accentColor="var(--c-red)"
        value={rules.lock_rules}
        canEdit={isAdmin}
        onSave={async (bilingualLists) => {
          await api.put("/admin/page-content", { rules_lock_override: bilingualLists });
          await load();
        }}
        testid="rules-lock"
      />

      <EditableRulesSection
        title={t("rules.section.visibility")}
        value={rules.visibility_rules}
        canEdit={isAdmin}
        onSave={async (bilingualLists) => {
          await api.put("/admin/page-content", { rules_visibility_override: bilingualLists });
          await load();
        }}
        testid="rules-visibility"
      />

      <EditableRulesSection
        title={t("rules.section.tiebreaker")}
        titleColor="var(--c-yellow)"
        borderColor="var(--c-yellow)"
        value={rules.tiebreaker}
        ordered
        canEdit={isAdmin}
        onSave={async (bilingualLists) => {
          await api.put("/admin/page-content", { rules_tiebreaker_override: bilingualLists });
          await load();
        }}
        testid="rules-tiebreaker"
      />

      <RulesPdfsPanel pdfs={rules.rules_pdfs || []} pools={pools} canEdit={isAdmin} onUpdated={load} />
    </div>
  );
}

function EditableRulesSection({ title, accent, accentColor, titleColor, borderColor, value, ordered, canEdit, onSave, testid }) {
  const { t, lang } = useI18n();
  const lines = pickList(value, lang);
  const bilingual = toBilingualList(value);
  const [editing, setEditing] = useState(false);
  const [draftEs, setDraftEs] = useState((bilingual.es || []).join("\n\n"));
  const [draftEn, setDraftEn] = useState((bilingual.en || []).join("\n\n"));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftEs((bilingual.es || []).join("\n\n"));
    setDraftEn((bilingual.en || []).join("\n\n"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const save = async () => {
    setSaving(true);
    try {
      const split = (s) => s.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
      await onSave({ es: split(draftEs), en: split(draftEn) });
      toast.success(t("rules.edit.saved"));
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    } finally {
      setSaving(false);
    }
  };

  const borderStyle = borderColor ? { borderLeft: `4px solid ${borderColor}` } : {};
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-6" style={borderStyle} data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        {accent && <div className="font-display text-3xl leading-none flex-shrink-0" style={{ color: accentColor }}>{accent}</div>}
        <div className="font-display text-2xl uppercase" style={titleColor ? { color: titleColor } : {}}>{title}</div>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} data-testid={`${testid}-edit`} className="ml-auto p-2 border border-zinc-800 hover:border-white" title={t("rules.edit.tooltip")}><Edit3 size={12} /></button>
        )}
      </div>
      {editing ? (
        <>
          <BilingualEditor
            value={{ es: draftEs, en: draftEn }}
            onChange={({ es, en }) => { setDraftEs(es); setDraftEn(en); }}
            multiline
            rows={Math.max(5, Math.max(draftEs.split("\n").length, draftEn.split("\n").length) + 1)}
            testidPrefix={testid}
          />
          <div className="text-[10px] text-zinc-500 font-mono-data mt-2 mb-2">{t("rules.edit.paraHelp")}</div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`${testid}-save`} className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-3 py-2 text-xs disabled:opacity-50 flex items-center gap-1">
              <Save size={12} /> {saving ? t("rules.edit.saving") : t("common.save")}
            </button>
            <button onClick={() => setEditing(false)} className="border border-zinc-800 px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-1"><X size={12} /> {t("common.cancel")}</button>
          </div>
        </>
      ) : (
        ordered ? (
          <ol className="text-zinc-200 text-base md:text-lg space-y-3 list-decimal pl-5 leading-relaxed text-justify">
            {lines.map((line, i) => <li key={`${i}-${line.slice(0, 24)}`}>{line}</li>)}
          </ol>
        ) : (
          lines.map((line, i) => (
            <p key={`${i}-${line.slice(0, 24)}`} className="text-zinc-300 text-base md:text-lg leading-relaxed mb-3 last:mb-0 text-justify" data-testid={`${testid}-line-${i}`}>{line}</p>
          ))
        )
      )}
    </div>
  );
}

function RulesPdfsPanel({ pdfs, pools, canEdit, onUpdated }) {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const [titleDrafts, setTitleDrafts] = useState({});
  // Initialize/sync drafts when pdfs change
  useEffect(() => {
    setTitleDrafts(Object.fromEntries(pdfs.map((p, i) => [i, p.title || ""])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pdfs)]);

  const persist = async (nextList) => {
    await api.put("/admin/page-content", {
      rules_pdfs: nextList,
      rules_pdf_url: null, // clear legacy single field once admin manages the new list
    });
    await onUpdated();
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error(t("rules.pdf.onlyPdf")); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error(t("rules.pdf.maxSize")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/admin/upload-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fallbackTitle = (file.name || "").replace(/\.pdf$/i, "").trim() || t("rules.pdf.title");
      // New PDFs start as global (empty pool_ids = visible to everyone)
      const newItem = { title: fallbackTitle, url: up.data?.url, pool_ids: [] };
      const nextList = [...pdfs, newItem];
      await persist(nextList);
      toast.success(t("rules.pdf.uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rules.pdf.uploadError"));
    } finally {
      setUploading(false);
    }
  };

  const handleRename = async (idx) => {
    const nextTitle = (titleDrafts[idx] || "").trim();
    if (!nextTitle) { toast.error(t("rules.pdf.titleRequired")); return; }
    if (nextTitle === pdfs[idx].title) return;
    const nextList = pdfs.map((p, i) => i === idx ? { ...p, title: nextTitle } : p);
    try {
      await persist(nextList);
      toast.success(t("rules.pdf.titleSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    }
  };

  const togglePool = async (idx, poolId) => {
    const current = pdfs[idx].pool_ids || [];
    const has = current.includes(poolId);
    const nextPoolIds = has ? current.filter((x) => x !== poolId) : [...current, poolId];
    const nextList = pdfs.map((p, i) => i === idx ? { ...p, pool_ids: nextPoolIds } : p);
    try {
      await persist(nextList);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    }
  };

  const setGlobal = async (idx) => {
    const nextList = pdfs.map((p, i) => i === idx ? { ...p, pool_ids: [] } : p);
    try {
      await persist(nextList);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    }
  };

  const handleRemove = async (idx) => {
    if (!confirm(t("rules.pdf.removeConfirm"))) return;
    try {
      const nextList = pdfs.filter((_, i) => i !== idx);
      await persist(nextList);
      toast.success(t("rules.pdf.removed"));
    } catch {
      toast.error(t("rules.pdf.removeError"));
    }
  };

  // Hide section entirely when there's nothing to show and user can't edit
  if (pdfs.length === 0 && !canEdit) return null;

  return (
    <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-4" data-testid="rules-pdf-section">
      <div className="flex items-center gap-3">
        <FileText className="text-[var(--c-lime)]" size={24} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl uppercase">{t("rules.pdf.title")}</div>
          <div className="text-zinc-400 text-sm">{t("rules.pdf.subtitle")}</div>
        </div>
        {canEdit && (
          <label className={`cursor-pointer inline-flex items-center gap-2 border border-dashed border-zinc-700 hover:border-[var(--c-lime)] hover:text-[var(--c-lime)] px-3 py-2 text-xs uppercase tracking-wider text-zinc-300 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload size={14} /> {t("rules.pdf.addAnother")}
            <input type="file" accept="application/pdf" className="hidden"
              data-testid="rules-pdf-upload"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUpload(f); }} />
          </label>
        )}
      </div>

      {pdfs.length === 0 ? (
        <div className="text-zinc-600 text-xs italic">{t("rules.pdf.empty")}</div>
      ) : (
        <ul className="space-y-2">
          {pdfs.map((pdf, idx) => {
            const href = pdfHref(pdf.url);
            const poolIds = pdf.pool_ids || [];
            const isGlobal = poolIds.length === 0;
            return (
              <li key={pdf.url} className="bg-black/40 border border-zinc-800 px-3 py-2 space-y-2" data-testid={`rules-pdf-item-${idx}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  {canEdit ? (
                    <input
                      value={titleDrafts[idx] ?? pdf.title ?? ""}
                      onChange={(e) => setTitleDrafts({ ...titleDrafts, [idx]: e.target.value })}
                      onBlur={() => handleRename(idx)}
                      data-testid={`rules-pdf-title-${idx}`}
                      placeholder={t("rules.pdf.titlePlaceholder")}
                      maxLength={120}
                      className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 focus:border-[var(--c-lime)] px-2 py-1.5 text-sm outline-none rounded-sm"
                    />
                  ) : (
                    <span className="flex-1 min-w-[180px] text-zinc-200 text-sm font-medium">{pdf.title || t("rules.pdf.title")}</span>
                  )}
                  {href && (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                      data-testid={`rules-pdf-link-${idx}`}
                      className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-3 py-2 text-xs flex items-center gap-2">
                      <FileText size={14} /> {t("rules.pdf.download")}
                    </a>
                  )}
                  {canEdit && (
                    <button onClick={() => handleRemove(idx)} data-testid={`rules-pdf-remove-${idx}`}
                      className="border border-[var(--c-red)] text-[var(--c-red)] px-2 py-2 text-xs uppercase tracking-wider hover:bg-[var(--c-red)] hover:text-black">
                      {t("rules.pdf.remove")}
                    </button>
                  )}
                </div>
                {canEdit && pools.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-zinc-800/60 mt-1">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{t("rules.pdf.visibleIn")}:</span>
                    <button
                      type="button"
                      onClick={() => isGlobal ? null : setGlobal(idx)}
                      data-testid={`rules-pdf-${idx}-pool-global`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${isGlobal ? "bg-[var(--c-mint)] text-black border-[var(--c-mint)] font-bold" : "border-zinc-700 text-zinc-400 hover:border-white"}`}
                    >
                      {t("rules.pdf.allPools")}
                    </button>
                    {pools.map((p) => {
                      const checked = poolIds.includes(p.pool_id);
                      return (
                        <button
                          key={p.pool_id}
                          type="button"
                          onClick={() => togglePool(idx, p.pool_id)}
                          data-testid={`rules-pdf-${idx}-pool-${p.pool_id}`}
                          className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${checked ? "bg-[var(--c-lime)] text-black border-[var(--c-lime)] font-bold" : "border-zinc-700 text-zinc-400 hover:border-white"}`}
                        >
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SectionTitle({ text }) {
  return <div className="font-display text-2xl md:text-3xl uppercase border-b border-zinc-800 pb-2">{text}</div>;
}

function RuleCard({ color, label, points, ptsLabel, canEdit, value, onSave, testid }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toBilingual(value));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(toBilingual(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const handleSave = async () => {
    if (!(draft.es || "").trim()) {
      toast.error("El texto en español no puede estar vacío");
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(t("rules.edit.saved"));
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="bg-zinc-900 border border-[var(--c-lime)] p-4 space-y-2" data-testid={`${testid}-editing`}>
        <BilingualEditor
          value={draft}
          onChange={setDraft}
          multiline
          rows={2}
          testidPrefix={testid}
        />
        <div className="flex gap-2 justify-end">
          <button onClick={() => setEditing(false)} className="border border-zinc-800 px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1"><X size={12} /> {t("common.cancel")}</button>
          <button onClick={handleSave} disabled={saving} data-testid={`${testid}-save`} className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-3 py-1.5 text-xs disabled:opacity-50 flex items-center gap-1">
            <Save size={12} /> {saving ? t("rules.edit.saving") : t("common.save")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 p-6 flex items-center gap-4 card-hover" data-testid={testid}>
      <div className="flex-shrink-0 flex items-center justify-center w-12">
        <span className="inline-block w-3 h-3 rounded-full bg-white" />
      </div>
      <div className="flex-1">
        <div className="text-base md:text-lg text-zinc-200 leading-relaxed">{label}</div>
      </div>
      <div className="text-right">
        <div className="font-display text-5xl leading-none" style={{ color }}>{points}</div>
        <div className="text-[10px] font-mono-data text-zinc-500 uppercase tracking-widest">{ptsLabel}</div>
      </div>
      {canEdit && (
        <button onClick={() => setEditing(true)} data-testid={`${testid}-edit`} className="p-2 border border-zinc-800 hover:border-white" title={t("rules.edit.tooltip")}>
          <Edit3 size={12} />
        </button>
      )}
    </div>
  );
}
